import {
  IconCheck,
  IconInfoCircle,
  IconMinus,
  IconPlus,
} from "@tabler/icons-react";
import clsx from "clsx";
import { useContext, useEffect, useRef, useState } from "react";
import { keyEnter } from "../Utils";
import { ModalContext } from "../contexts/Contexts";
import { Button, shake } from "./Button";
import Tooltip from "./Tooltip";

/**
 * @param {{
 *   className: string;
 *   label: string;
 *   icon: "check" | "plus" | "minus";
 *   variant: ButtonProps["variant"];
 *   bgColor: string;
 *   state: [boolean, ButtonProps["onClick"]];
 *   info: JSX.Element;
 *   disabled: boolean;
 *   radio: boolean;
 *   after: any;
 * }} props
 */
const CheckBox = (props) => {
  const {
    className,
    label,
    icon = "check",
    variant,
    bgColor,
    state,
    info,
    disabled,
    radio,
    after,
  } = props;

  const [selected, toggleSelected] = state || [];
  const { contentElement } = useContext(ModalContext);
  const inactive = !selected || disabled;
  const tooltipRef = useRef(null);
  const checkRef = useRef(null);

  /** @param {MouseEvent} event */
  function handleToggleBtn(event) {
    if (disabled) {
      shake(event.target);
      return;
    }
    toggleSelected?.(event);
  }

  /** @param {MouseEvent} event */
  function clickOutsideCheck(event) {
    if (!event.target.matches(".check-box")) return;
    checkRef.current?.click();
  }

  function infoClick() {
    const tooltip = tooltipRef.current;
    tooltip?.state.isVisible ? tooltip?.hide() : tooltip?.show();
  }

  const checkIcon = radio ? (
    <div className="radio" />
  ) : icon === "check" ? (
    <IconCheck stroke={3} />
  ) : icon === "plus" ? (
    <IconPlus stroke={3} />
  ) : icon === "minus" ? (
    <IconMinus stroke={3} />
  ) : null;

  return (
    <span
      className={clsx(
        "check-box",
        radio && "radio",
        className,
        !inactive && "selected",
        disabled && "disabled",
      )}
      onClick={clickOutsideCheck}
      tabIndex={-1}
    >
      <Button
        className="check"
        icon={checkIcon}
        depth="0.4rem"
        fontSize="1rem"
        bgColor={bgColor}
        variant={variant || (inactive && "secondary")}
        onClick={handleToggleBtn}
        tabIndex={disabled && -1}
        ref={checkRef}
      />
      <span
        className="label"
        style={{ marginRight: !info && "auto", display: !label && "none" }}
      >
        {label}
      </span>
      {info && (
        <Tooltip
          className={clsx("info-tooltip", className && `tt-${className}`)}
          content={info}
          appendTo={contentElement}
          trigger="mouseenter focus click"
          delay={[500, 0]}
          placement="right"
          onCreate={(inst) => (tooltipRef.current = inst)}
        >
          <IconInfoCircle
            className="info-hint"
            stroke={2.2}
            tabIndex={0}
            style={{ marginRight: info && "auto" }}
            onKeyDown={(e) => keyEnter(e, infoClick)}
          />
        </Tooltip>
      )}
      {after}
    </span>
  );
};

/**
 * @param {React.ComponentProps<CheckBox> & {
 *   someSelected: boolean;
 *   allSelected: boolean;
 *   onSelectAll: Function;
 *   onDeselectAll: Function;
 * }} props
 */
export const MasterCheckBox = (props) => {
  const {
    radio, // unused

    someSelected,
    allSelected,
    onSelectAll,
    onDeselectAll,
    ...otherProps
  } = props;

  const [shiftPressed, setShiftPressed] = useState(false);
  const [selectHovered, setSelectHovered] = useState(false);

  window.onkeydown = (event) => setShiftPressed(event.shiftKey);
  window.onkeyup = (event) => setShiftPressed(event.shiftKey);

  useEffect(() => {
    const onKeyDown = (event) => setShiftPressed(event.shiftKey);
    const onKeyUp = (event) => setShiftPressed(event.shiftKey);

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  function selectAll(event) {
    if (shiftPressed) {
      if (!someSelected) return;
      return onDeselectAll?.(event);
    }

    if (allSelected) return;
    onSelectAll?.(event);
  }

  function deselectAll(event) {
    if (shiftPressed) {
      if (allSelected) return;
      return onSelectAll?.(event);
    }

    if (!someSelected) return;
    onDeselectAll?.(event);
  }

  return (
    <CheckBox
      {...otherProps}
      state={[someSelected, someSelected ? deselectAll : selectAll]}
      icon={allSelected ? "check" : shiftPressed ? "plus" : "minus"}
    />
  );
};

export default CheckBox;
